import type { DialogueTree } from './types';

/**
 * Dad. Tone: terse, warm-but-can\'t-say-it, opens texts with "son" or just "."
 * Cares deeply, expresses it through asking about the lawn / car / weather.
 * Will never use a paragraph break. Will text "ok." in response to "i love you."
 */
export const DAD_TREE: DialogueTree = {
  contactId: 'dad',
  rootChoices: [
    {
      id: 'i-love-you',
      label: 'tell him you love him',
      playerSays: "i love you dad",
      effects: [{ type: 'rep', target: 'mom', value: 4 }, { type: 'sanity', value: 5 }, { type: 'note', value: 'told dad you love him. he won\'t say it back. he heard you.' }],
      responses: [
        { text: "ok. love you too son.\n\nhow\'s your car running.",
          followUps: [
            { id: 'love-car-fine', label: "car\'s fine dad",
              playerSays: "car\'s fine dad",
              effects: [{ type: 'sanity', value: 2 }],
              responses: [
                { text: "good. check the air in the back tires.\n\ndad" },
              ],
            },
          ],
        },
        { text: "ok son. proud of you. dad",
          effects: [{ type: 'sanity', value: 4 }] },
        { text: "ok.\n\ndad",
          effects: [{ type: 'sanity', value: 2 }] },
      ],
    },

    {
      id: 'check-on-mom',
      label: 'ask how mom is doing',
      playerSays: "is mom okay",
      responses: [
        { text: "she\'s your mother. she\'s always okay and she\'s never okay.\n\nshe just wants you to call. dad",
          followUps: [
            { id: 'check-call-mom', label: "i\'ll call her tonight",
              playerSays: "i\'ll call her tonight dad",
              effects: [{ type: 'rep', target: 'mom', value: 4 }],
              responses: [
                { text: "good son.\n\ndad" },
              ],
            },
          ],
        },
        { text: "she\'s fine. tell her you noticed her hair.\n\ndad" },
      ],
    },

    {
      id: 'ask-his-day',
      label: 'ask about his day',
      playerSays: "how was your day dad",
      effects: [{ type: 'rep', target: 'mom', value: 2 }],
      responses: [
        { text: "fine. mowed the lawn. it didn\'t need it.\n\ndad" },
        { text: "okay. went to the hardware store. didn\'t buy anything. just looked.\n\ndad" },
        { text: "decent. saw a deer. it was bigger than last year.\n\ndad" },
      ],
    },

    {
      id: 'ask-advice',
      label: 'ask for life advice',
      playerSays: "dad i need advice",
      responses: [
        { text: "ok son.\n\nshoot.",
          followUps: [
            { id: 'advice-marriage', label: "stuff with the wife",
              playerSays: "stuff with the wife",
              effects: [{ type: 'sanity', value: 4 }, { type: 'rep', target: 'wife', value: 1 }],
              responses: [
                { text: "ok son.\n\nyou listen more than you talk. you ask about her day before yours. you do the dishes when she\'s upset and you don\'t ask why.\n\nworked for me. mostly. dad" },
              ],
            },
            { id: 'advice-money', label: "money stuff",
              playerSays: "money stuff",
              effects: [{ type: 'sanity', value: 3 }],
              responses: [
                { text: "ok son.\n\nlive below your means. always. that\'s it. that\'s the whole thing.\n\nyou\'re not living below your means. i can tell. dad" },
              ],
            },
            { id: 'advice-work', label: "work stuff",
              playerSays: "work stuff",
              effects: [{ type: 'sanity', value: 3 }],
              responses: [
                { text: "ok son.\n\nshow up. don\'t complain. do the boring parts well. the rest takes care of itself.\n\nyour job is, frankly, not a real job. but the principle holds. dad" },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'tell-loss',
      label: 'tell him you took a loss',
      playerSays: "dad i had a bad week. lost some money.",
      effects: [{ type: 'sanity', value: 5 }],
      responses: [
        { text: "ok son.\n\nhow much.\n\ndad",
          followUps: [
            { id: 'loss-small', label: "few thousand",
              playerSays: "few thousand. nothing crazy.",
              effects: [{ type: 'sanity', value: 4 }],
              responses: [
                { text: "ok son.\n\nyou\'ve had worse. you\'ll have worse. eat dinner. dad" },
              ],
            },
            { id: 'loss-big', label: "a lot. like, a lot.",
              playerSays: "a lot dad. a real lot.",
              effects: [{ type: 'sanity', value: 6 }, { type: 'rep', target: 'mom', value: 3 }, { type: 'note', value: 'told dad about big loss. he\'ll tell mom. he\'ll come visit.' }],
              responses: [
                { text: "ok son.\n\ndo not tell your mother. i will tell your mother in my own way. or not at all. depends.\n\ni\'ll come up this weekend. don\'t make a fuss. dad" },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'ask-his-history',
      label: 'ask about his history',
      playerSays: "did you ever go through anything like this dad",
      effects: [{ type: 'sanity', value: 5 }],
      responses: [
        { text: "ok son.\n\n1987. that was mine.\n\nlost most of what i had. didn\'t tell your mother for 6 weeks. she figured it out anyway. she always did.\n\ngot through it. you will. dad" },
        { text: "ok son.\n\nyes.\n\nnot the same shape as yours but yes. the shape doesn\'t matter. the part where you don\'t sleep is universal. dad" },
      ],
    },

    {
      id: 'lawn-talk',
      label: 'talk about the lawn',
      playerSays: "how\'s the lawn doing dad",
      effects: [{ type: 'sanity', value: 2 }],
      responses: [
        { text: "the johnson grass is back. i\'m fighting it. it\'s winning.\n\ndad" },
        { text: "good. mowed monday. sprinkler timer is acting up. i\'ll figure it out. dad" },
        { text: "patchy. the dog from across the street is using it as his personal facility. i have words pending. dad" },
      ],
    },

    {
      id: 'send-dad-money',
      label: "send dad \$500 (he won\'t admit needing it)",
      playerSays: "[wired \$500]",
      requires: (ctx) => ctx.cash >= 500,
      effects: [
        { type: 'cash', value: -500 },
        { type: 'sanity', value: 4 },
        { type: 'rep', target: 'mom', value: 6 },
        { type: 'note', value: '-$500 to dad. mom rep +6 (she sees the wire).' },
      ],
      responses: [
        { text: "ok son.\n\nyou didn\'t have to.\n\nmother is, currently, on the phone with her sister telling her you sent it. she will tell everyone. that\'s the price of doing this.\n\nthank you.\n\ndad" },
      ],
    },

    {
      id: 'visit-this-weekend',
      label: 'tell him you\'ll visit this weekend',
      playerSays: "coming up this weekend dad. friday night through sunday.",
      effects: [{ type: 'sanity', value: 5 }, { type: 'rep', target: 'mom', value: 8 }, { type: 'rep', target: 'wife', value: 3 }],
      responses: [
        { text: "ok son.\n\nyour mother is, in this exact moment, deciding what to cook. it will be brisket. it is always brisket. wear the shirt without the chart on it.\n\ndad" },
      ],
    },

    {
      id: 'apologize-distance',
      label: 'apologize for being distant',
      playerSays: "dad. i\'ve been a bad son. i\'m sorry.",
      effects: [{ type: 'sanity', value: 6 }, { type: 'rep', target: 'mom', value: 4 }],
      responses: [
        { text: "ok son.\n\nyou\'re a good son. you\'re a busy son. those are different things. i know which.\n\ncall your mother. she\'s waiting.\n\ndad" },
      ],
    },

    {
      id: 'snap-at-dad',
      label: 'snap at him',
      destructive: true,
      playerSays: "dad i don\'t need a lecture today.",
      effects: [{ type: 'sanity', value: -4 }, { type: 'rep', target: 'mom', value: -3 }],
      responses: [
        { text: "ok son.\n\nthat wasn\'t a lecture. when i lecture you, you\'ll know.\n\ndad" },
      ],
    },
  ],
};
